"use client";

type WorkerReply = { ok: boolean; ready: boolean; error?: string };

function supported() {
  return typeof navigator !== "undefined" && "serviceWorker" in navigator;
}

function withTimeout<T>(
  promise: Promise<T>,
  milliseconds: number,
  message: string,
) {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error(message)), milliseconds);
    promise.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (error: unknown) => {
        clearTimeout(timer);
        reject(error);
      },
    );
  });
}

export async function registerOfflineServiceWorker() {
  if (!supported())
    throw new Error("This browser does not support offline workspaces.");
  const registration = await withTimeout(
    navigator.serviceWorker.register("/sw.js", {
      scope: "/",
      updateViaCache: "none",
    }),
    15_000,
    "Offline setup timed out. Check your connection and try again.",
  );
  const update = registration.installing ?? registration.waiting;
  if (update && update.state !== "activated") {
    await new Promise<void>((resolve, reject) => {
      const timer = setTimeout(() => {
        update.removeEventListener("statechange", changed);
        reject(
          new Error(
            "The offline update is not ready. Reload while online and try again.",
          ),
        );
      }, 15_000);
      function changed() {
        if (update!.state !== "activated" && update!.state !== "redundant")
          return;
        clearTimeout(timer);
        update!.removeEventListener("statechange", changed);
        if (update!.state === "activated") resolve();
        else
          reject(
            new Error(
              "The offline update could not install. Try again online.",
            ),
          );
      }
      update.addEventListener("statechange", changed);
      changed();
    });
  }
  return withTimeout(
    navigator.serviceWorker.ready,
    15_000,
    "The offline service worker is not ready. Reload while online and try again.",
  );
}

function askWorker(
  worker: ServiceWorker,
  type: "OFFLINE_PREPARE" | "OFFLINE_STATUS",
) {
  return new Promise<WorkerReply>((resolve, reject) => {
    const channel = new MessageChannel();
    const timer = setTimeout(
      () => {
        close();
        reject(
          new Error(
            "Offline setup did not respond. Reload while online and try again.",
          ),
        );
      },
      type === "OFFLINE_PREPARE" ? 120_000 : 5_000,
    );
    function close() {
      clearTimeout(timer);
      channel.port1.close();
      channel.port2.close();
    }
    channel.port1.onmessage = (event: MessageEvent<WorkerReply>) => {
      close();
      resolve(event.data);
    };
    channel.port1.onmessageerror = () => {
      close();
      reject(
        new Error(
          "The offline service worker returned an unreadable response.",
        ),
      );
    };
    try {
      worker.postMessage({ type }, [channel.port2]);
    } catch (error) {
      close();
      reject(error);
    }
  });
}

/** Resolves only after public HTML and all required static files commit to cache. */
export async function prepareOfflineWorkspace(): Promise<void> {
  const registration = await registerOfflineServiceWorker();
  if (!registration.active)
    throw new Error("The offline service worker is not ready.");
  const reply = await askWorker(registration.active, "OFFLINE_PREPARE");
  if (!reply.ok || !reply.ready) {
    throw new Error(
      reply.error ??
        "The offline workspace is not ready. Prepare it again online.",
    );
  }
}

/** Checks stored files, including after a reload or possible browser eviction. */
export async function isOfflineWorkspacePrepared(): Promise<boolean> {
  if (!supported()) return false;
  try {
    const registration = await navigator.serviceWorker.getRegistration("/");
    if (!registration?.active) return false;
    const reply = await askWorker(registration.active, "OFFLINE_STATUS");
    return reply.ok === true && reply.ready === true;
  } catch {
    return false;
  }
}
